/**
 * Custom hook for PowerEditor state management
 */

import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ViewportSize, ViewportSizeType } from "@/types/viewport-device.types";
import {
  ViewMode,
  Workspace,
  BuilderData,
  AppService,
} from "@/types/power-editor.types";
import {
  DEFAULT_CUSTOM_VIEWPORT,
} from "@/constants/power-editor.constants";
import { usePagesStore } from "@/stores/pages-store";
import { resolveAppServiceFromPort } from "@/lib/utils/power-editor-utils";
import { devContainerAPI } from "@/lib/dev-containers/api";
import { projectsApi, Project } from "@/lib/api/projects";
import { editorAPI } from "@/lib/editor/api";
import { useAuth } from "@/contexts/AuthContext";
import { useProjectStore } from "@/stores/projectStore";

export function usePowerEditorState() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const projectId = params.projectId as string;
  const { getIdToken } = useAuth();
  const { setCurrentProject, currentProject } = useProjectStore();

  // Main state
  const [project, setProject] = useState<Project | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessUrl, setAccessUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isShowingStaticPreview, setIsShowingStaticPreview] = useState(false);
  const [appService, setAppService] = useState<AppService | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // UI state
  const [viewMode, setViewMode] = useState<ViewMode>("agent");
  const [isSidebarHidden, setIsSidebarHidden] = useState<boolean>(false);
  const [isFullscreenChat, setIsFullscreenChat] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(true);
  const [isPreviewDisabled, setIsPreviewDisabled] = useState<boolean>(false);
  const [isSavingChanges, setIsSavingChanges] = useState<boolean>(false);
  const [commitState, setCommitState] = useState<
    "idle" | "generating" | "creating"
  >("idle");
  const [showMobileTabSwitcher, setShowMobileTabSwitcher] = useState<boolean>(true);
  const [mobileActiveTab, setMobileActiveTab] = useState<"chat" | "preview" | "visual">("chat");
  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(true);

  // Responsive state
  const [isResponsiveMode, setIsResponsiveMode] = useState<boolean>(false);
  const [viewportSize, setViewportSize] = useState<ViewportSizeType>(
    ViewportSize.desktop
  );
  const [isCustomViewport, setIsCustomViewport] = useState<boolean>(false);
  const [customWidth, setCustomWidth] = useState<number>(
    DEFAULT_CUSTOM_VIEWPORT.width
  );
  const [customHeight, setCustomHeight] = useState<number>(
    DEFAULT_CUSTOM_VIEWPORT.height
  );

  // Navigation state
  const [currentPath, setCurrentPath] = useState<string>("/");
  const availablePaths = usePagesStore((s) => s.pages);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState<boolean>(false);
  const [showPathDropdown, setShowPathDropdown] = useState<boolean>(false);

  // From app library remix
  const fromApp = searchParams.get('from') === 'app';

  // Builder data
  const [builderData, setBuilderData] = useState<BuilderData | null>(null);

  // Handler to mark builder data as processed (called by AgentModeContainer)
  const handleBuilderDataProcessed = () => {
    console.log('[PowerEditor] Marking builder data as processed locally');
    setBuilderData(prev => prev ? { ...prev, isProcessed: true } : null);
  };

  // Agent state
  const [isAgentStreaming, setIsAgentStreaming] = useState<boolean>(false);

  // Refs
  const [previewIframeRef, setPreviewIframeRef] =
    useState<React.RefObject<HTMLIFrameElement> | null>(null);
  const initialPathRef = useRef<string>(currentPath);
  const pollingCleanupRef = useRef<(() => void) | null>(null);
  const previewIframeForPollingRef = useRef<HTMLIFrameElement | null>(null);

  // Check if running in development mode
  const isDevelopment = window.location.hostname === "localhost";

  // Load builder data from PostgreSQL
  useEffect(() => {
    if (!projectId) return;

    const loadBuilderData = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await projectsApi.getBuilderData(token, projectId);
        const data = response.builderData;
        const isProcessed = response.builderDataProcessed;

        if (data && data.fromBuilder) {
          console.log(
            "[PowerEditor] Loaded builder data from PostgreSQL:",
            data,
            "isProcessed:",
            isProcessed
          );
          setBuilderData({
            fromBuilder: true,
            initialPrompt: data.initialPrompt || "",
            appType: data.appType || 'unknown',
            tags: data.tags || [],
            mode: data.mode || 'execute',
            referenceUrls: data.referenceUrls,
            uploadedFileIds: data.uploadedFileIds,
            uploadedFiles: data.uploadedFiles,
            isProcessed: isProcessed,
          });
        } else {
          console.log(
            "[PowerEditor] No builder data found in PostgreSQL"
          );
        }
      } catch (error) {
        console.error(
          "[PowerEditor] Failed to load builder data from PostgreSQL:",
          error
        );
      }
    };

    loadBuilderData();
  }, [projectId, getIdToken]);

  // Close device dropdown when responsive mode is disabled
  useEffect(() => {
    if (!isResponsiveMode) {
      setShowDeviceDropdown(false);
    }
  }, [isResponsiveMode]);

  // Sync URL params to viewMode state on mount and when params change
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "settings") {
      setViewMode("settings");
    } else if (viewMode === "settings") {
      // Reset to agent when navigating away from settings
      setViewMode("agent");
    }
  }, [searchParams]);

  // Sync local project state with zustand store when project name/urlSlug changes (e.g., after rename)
  useEffect(() => {
    if (currentProject && project && currentProject.id === project.id) {
      // Only update if name or urlSlug changed
      if (currentProject.name !== project.name || currentProject.urlSlug !== project.urlSlug) {
        setProject((prev) => prev ? { ...prev, name: currentProject.name, urlSlug: currentProject.urlSlug } : null);
      }
    }
  }, [currentProject?.name, currentProject?.urlSlug]);

  // Auto-collapse sidebar when in editor mode
  useEffect(() => {
    document.body.classList.add("editor-mode");

    return () => {
      document.body.classList.remove("editor-mode");
    };
  }, []);

  // Get preview URL (token-authenticated) - uses initial path only
  const computedPreviewUrl = useMemo(() => {
    if (!workspace) return "";

    const pathForUrl = initialPathRef.current;

    // In development, use direct localhost URL with initial path
    if (isDevelopment && appService) {
      const devPort = import.meta.env.VITE_PREVIEW_PORT || "5173";
      const baseUrl = `http://localhost:${devPort}`;

      if (pathForUrl && pathForUrl !== "/") {
        const cleanPath = pathForUrl.startsWith("/")
          ? pathForUrl
          : `/${pathForUrl}`;
        return `${baseUrl}${cleanPath}`;
      }

      return baseUrl;
    }

    // In production, use the token-authenticated preview URL with route
    const baseUrl = previewUrl || "";
    if (!baseUrl) return "";

    try {
      const url = new URL(baseUrl);
      const token = url.searchParams.get("tkn");

      if (pathForUrl && pathForUrl !== "/") {
        const cleanPath = pathForUrl.startsWith("/")
          ? pathForUrl
          : `/${pathForUrl}`;

        return token
          ? `${url.origin}${cleanPath}?tkn=${token}`
          : `${url.origin}${cleanPath}`;
      }
      console.log(
        "[PowerEditor] Computed preview URL (root path) with token",
        baseUrl
      );
      return baseUrl;
    } catch (error) {
      console.error("[PowerEditor] Failed to parse preview URL:", error);
      return baseUrl;
    }
  }, [workspace, isDevelopment, appService, previewUrl]);

  // Get IDE URL (token-authenticated) - memoized
  const computedIdeUrl = useMemo(() => {
    if (!workspace) return "";

    // In development, use proxy URL
    if (isDevelopment) {
      return `/api/dev-containers/proxy/${workspace.subdomain}/ide`;
    }

    // Use the token-authenticated IDE URL
    return accessUrl || "";
  }, [workspace, isDevelopment, accessUrl]);

  // Get actual preview URL with current path - used for reload and opening in new window
  const computedActualPreviewUrl = useMemo(() => {
    if (!workspace) return "";

    // In development, use direct localhost URL with current path
    if (isDevelopment && appService) {
      const devPort = import.meta.env.VITE_PREVIEW_PORT || "5173";
      const baseUrl = `http://localhost:${devPort}`;

      if (currentPath && currentPath !== "/") {
        const cleanPath = currentPath.startsWith("/")
          ? currentPath
          : `/${currentPath}`;
        return `${baseUrl}${cleanPath}`;
      }

      return baseUrl;
    }

    // In production, use URL WITHOUT token
    const baseUrl = previewUrl || "";
    if (!baseUrl) return "";

    try {
      const url = new URL(baseUrl);

      if (currentPath && currentPath !== "/") {
        const cleanPath = currentPath.startsWith("/")
          ? currentPath
          : `/${currentPath}`;

        return `${url.origin}${cleanPath}`;
      }

      return url.origin;
    } catch (error) {
      console.error("[PowerEditor] Failed to parse preview URL:", error);
      return baseUrl;
    }
  }, [workspace, isDevelopment, appService, previewUrl, currentPath]);

  return {
    // State
    project,
    workspace,
    loading,
    error,
    accessUrl,
    previewUrl,
    isResuming,
    isPreviewLoading,
    isShowingStaticPreview,
    appService,
    retryCount,
    viewMode,
    isSidebarHidden,
    isFullscreenChat,
    showPreview,
    isPreviewDisabled,
    isSavingChanges,
    commitState,
    showMobileTabSwitcher,
    mobileActiveTab,
    isHeaderVisible,
    isResponsiveMode,
    viewportSize,
    isCustomViewport,
    customWidth,
    customHeight,
    currentPath,
    availablePaths,
    showDeviceDropdown,
    showPathDropdown,
    fromApp,
    builderData,
    handleBuilderDataProcessed,
    isAgentStreaming,
    previewIframeRef,
    initialPathRef,
    pollingCleanupRef,
    previewIframeForPollingRef,
    isDevelopment,
    computedPreviewUrl,
    computedIdeUrl,
    computedActualPreviewUrl,
    projectId,

    // Setters
    setProject,
    setCurrentProject,
    setWorkspace,
    setLoading,
    setError,
    setAccessUrl,
    setPreviewUrl,
    setIsResuming,
    setIsPreviewLoading,
    setIsShowingStaticPreview,
    setAppService,
    setRetryCount,
    setViewMode,
    setIsSidebarHidden,
    setIsFullscreenChat,
    setShowPreview,
    setIsPreviewDisabled,
    setIsSavingChanges,
    setCommitState,
    setShowMobileTabSwitcher,
    setMobileActiveTab,
    setIsHeaderVisible,
    setIsResponsiveMode,
    setViewportSize,
    setIsCustomViewport,
    setCustomWidth,
    setCustomHeight,
    setCurrentPath,
    setShowDeviceDropdown,
    setShowPathDropdown,
    setBuilderData,
    setIsAgentStreaming,
    setPreviewIframeRef,
    setPreviewIframeForPollingRef: (ref: HTMLIFrameElement | null) => {
      previewIframeForPollingRef.current = ref;
    },
  };
}